import { IEmployeeRepository } from './repository';
import { checkPermission } from 'utils/Deecorators';

export class EmployeeRepository implements IEmployeeRepository {
    private httpService: IHttpService;

    constructor(instance: IHttpService) {
        this.httpService = instance;
    }

    @checkPermission('employee.GetEmployees')
    async getEmployees({ start_date, end_date, params }: { start_date?: string; end_date?: string; params?: Record<string, any> }): Promise<Employee[]> {
        const response = await this.httpService.get<TKResponse<Employee[]>>(`/employee`, { params: { ...params, start_date, end_date, without_hiring: true } });
        return response.data.sort((a, b) => ((a as any).name ?? '').localeCompare((b as any).name ?? ''));
    }

    async createEmployee(data: EmployeeBase): Promise<Employee> {
        const moment = await import('moment');
        data.pay_period_end = moment.default().subtract(1, 'day').format('YYYY-MM-DD');

        return this.httpService.post<Employee, EmployeeBase>('/employee/create', { data });
    }

    async deleteEmployee(id: number): Promise<number> {
        return this.httpService.delete<number>(`/employee/${id}`);
    }

    async updateEmployee(data: Employee): Promise<Employee> {
        return this.httpService.patch<Employee, Employee>(`/employee/${data.employee_id}`, { data });
    }

    async getDocumentTypes(): Promise<DocumentTyp[]> {
        const response = await this.httpService.get<TKResponse<DocumentTyp[]>>('/document_type');
        return response.data;
    }

    @checkPermission('employee.GetAllSubCompanies')
    async getSubcompany(): Promise<Subcompany[]> {
        const response = await this.httpService.get<TKResponse<Subcompany[]>>('/sub_company');
        return response.data;
    }

    @checkPermission('employee.GetAllArea')
    async getArea(params: Record<string, any> = {}): Promise<Area[]> {
        const response = await this.httpService.get<TKResponse<Area[]>>('/area', { params });
        return response.data;
    }

    @checkPermission('employee.GetAllWorkPositions')
    async getWorkPosition(params: Record<string, any> = {}): Promise<WorkPosition[]> {
        const response = await this.httpService.get<TKResponse<WorkPosition[]>>('/work_position', { params });
        return response.data;
    }

    async assignHiringRange(data: Record<string, any>): Promise<boolean> {
        return this.httpService.post<boolean, Record<string, any>>('/hiring', { data });
    }

    async assignHiringOrgpath(data: Record<string, any>): Promise<boolean> {
        return this.httpService.post<boolean, Record<string, any>>('/orgpath', { data });
    }

    @checkPermission('employee.GetHiringRanges')
    async getHiringRange(params: Record<string, any> = {}): Promise<HiringRange[]> {
        const response = await this.httpService.get<TKResponse<HiringRange[]>>('/hiring_range', { params });
        return response.data;
    }

    async getHiringRangeById(id: number): Promise<HiringRange> {
        const response = await this.httpService.get<TKResponse<HiringRange>>(`/hiring_range/${id}`);
        return response.data;
    }

    async createHiringRange(data: { employee_id: number; start_date: string; end_date: string }): Promise<HiringRange> {
        const response = await this.httpService.post<TKResponse<HiringRange>, typeof data>('/hiring_range/create', { data });
        return response.data;
    }

    async updateHiringRange(id: number, data: { employee_id: number; start_date: string; end_date: string }): Promise<HiringRange> {
        const response = await this.httpService.patch<TKResponse<HiringRange>, typeof data>(`/hiring_range/${id}`, { data });
        return response.data;
    }

    async deleteHiringRange(id: number): Promise<void> {
        await this.httpService.delete<void>(`/hiring_range/${id}`);
    }

    async getHiring(params: Record<string, any> = {}): Promise<any> {
        const response = await this.httpService.get<any>('/hiring', { params });
        return response.data;
    }

    async getHiringById(id: number): Promise<Hiring> {
        const response = await this.httpService.get<TKResponse<Hiring>>(`/hiring/${id}`);
        return response.data;
    }

    async createHiring(data: { hiring_range_id: number; name_hiring: string; type_hiring: string; position: string; enable: boolean; automatic_renewal: boolean }): Promise<Hiring> {
        const response = await this.httpService.post<TKResponse<Hiring>, typeof data>('/hiring/create', { data });
        return response.data;
    }

    async updateHiring(id: number, data: { name_hiring: string; type_hiring: string; position: string; enable: boolean; automatic_renewal: boolean }): Promise<Hiring> {
        const response = await this.httpService.patch<TKResponse<Hiring>, typeof data>(`/hiring/${id}`, { data });
        return response.data;
    }

    async deleteHiring(id: number): Promise<void> {
        await this.httpService.delete<void>(`/hiring/${id}`);
    }

    async getHiringOrgpath(params: Record<string, any> = {}): Promise<any> {
        const response = await this.httpService.get<any>('/hiring_orgpath', { params });
        return response.data;
    }

    async getHiringOrgpathDetail(id: number): Promise<any> {
        const response = await this.httpService.get<any>(`/hiring_orgpath/${id}/detail`);
        return response.data;
    }

    async terminateEmployee(hiring_range_id: number, data: Record<string, any>): Promise<any> {
        const response = await this.httpService.patch<any, Object>(`/employee/terminate_employee/${hiring_range_id}`, { data });
        return response.data;
    }
    async getTags(): Promise<Tag[]> {
        const response = await this.httpService.get<ResponseData<TagResponse[]>>('/tag');

        const data = response.data.map((tag) => ({
            ...tag,
            id: tag.tag_id,
            name: tag.tag_name,
        }));
        return data;
    }
    async createTag(name: string): Promise<Tag> {
        const response = await this.httpService.post<ResponseData<Tag>, { name: string }>('/tag/create', { data: { name } });
        return response.data;
    }

    async getEmployeeTags(tags: number[]): Promise<Tag[]> {
        const response = await this.httpService.get<ResponseData<Tag[]>>(`/employee_r_tag`, {
            params: { tags_id: JSON.stringify(tags) },
        });
        return response.data;
    }

    async assignTagsToEmployee(employee_ids: number[], tag_ids: number[], start_date: string, end_date: string, overwrite: boolean = false): Promise<boolean> {
        const response = await this.httpService.post<boolean, { employee_ids: number[]; tag_ids: number[]; start_date: string; end_date: string; overwrite: boolean }>(
            `/employee_r_tag/assign_employee_to_tag`,
            { data: { employee_ids, tag_ids, start_date, end_date, overwrite } },
        );
        return response;
    }

    async getTagsByEmployee(employee_id: number): Promise<EmployeeTagRange[]> {
        const response = await this.httpService.get<ResponseData<EmployeeTagRange[]>>(`/employee_r_tag/by_employee/${employee_id}`);
        return response.data;
    }

    async getEmployeePayRules(employee_id: number): Promise<EmployeeRPayRule[]> {
        const response = await this.httpService.get<TKResponse<EmployeeRPayRule[]>>(`/employee_r_pay_rule`, {
            params: { employee_id },
        });
        return response.data;
    }

    async createEmployeePayRule(data: CreateEmployeeRPayRule): Promise<EmployeeRPayRule> {
        const response = await this.httpService.post<TKResponse<EmployeeRPayRule>, CreateEmployeeRPayRule>(`/employee_r_pay_rule`, { data });
        return response.data;
    }

    async updateEmployeePayRule(id: number, data: UpdateEmployeeRPayRule): Promise<EmployeeRPayRule> {
        const response = await this.httpService.patch<TKResponse<EmployeeRPayRule>, UpdateEmployeeRPayRule>(`/employee_r_pay_rule/${id}`, { data });
        return response.data;
    }

    async deleteEmployeePayRule(id: number): Promise<void> {
        await this.httpService.delete<void>(`/employee_r_pay_rule/${id}`);
    }

    async setEmployeePayRules(employee_id: number, data: Array<{ pay_rule_id: number; start_date: string; end_date: string }>): Promise<void> {
        await this.httpService.patch<void, typeof data>(`/employee/${employee_id}/pay_rule`, { data });
    }
}
